import { useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SAStat, SALoading, fmtMoney, fmtDateTime } from "./saCommon";
import { Buildings, UsersThree, CreditCard, Money, Warning, TrendUp, ClockCountdown, Receipt } from "@phosphor-icons/react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function SADashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    saFetch("/superadmin/dashboard").then(setData).catch((e) => toast.error(e.message));
  }, []);

  if (!data) return <SALoading label="Cargando panel..." />;

  return (
    <div data-testid="sa-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Visión general de la plataforma</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SAStat icon={Buildings} label="Empresas" value={data.total_empresas} hint={`${data.empresas_activas} activas · ${data.empresas_inactivas} inactivas`} testid="sa-stat-empresas" />
        <SAStat icon={UsersThree} label="Usuarios activos" value={data.usuarios_activos} tone="sky" testid="sa-stat-usuarios" />
        <SAStat icon={CreditCard} label="Suscripciones" value={data.subs_mensuales + data.subs_anuales} hint={`${data.subs_mensuales} mensuales · ${data.subs_anuales} anuales`} tone="violet" testid="sa-stat-subs" />
        <SAStat icon={ClockCountdown} label="Por vencer (7d)" value={data.por_vencer} tone="amber" testid="sa-stat-vencer" />
        <SAStat icon={Receipt} label="Pagos pendientes" value={data.pagos.pendientes} hint={`${data.pagos.aprobados} aprobados · ${data.pagos.rechazados} rechazados`} tone="amber" testid="sa-stat-pagos" />
        <SAStat icon={Money} label="Ingresos del mes" value={fmtMoney(data.ingresos_mes)} tone="emerald" testid="sa-stat-ingresos-mes" />
        <SAStat icon={TrendUp} label="Ingresos acumulados" value={fmtMoney(data.ingresos_total)} tone="emerald" testid="sa-stat-ingresos-total" />
        <SAStat icon={Warning} label="Sin actividad (14d)" value={data.sin_actividad.length} tone="red" testid="sa-stat-inactivas" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="border border-border bg-card rounded-sm p-5" data-testid="sa-chart-clients">
          <h3 className="font-heading text-base font-bold text-foreground mb-4">Nuevos clientes</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.nuevos_clientes}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mes" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }} />
              <Bar dataKey="nuevos" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border bg-card rounded-sm p-5" data-testid="sa-chart-revenue">
          <h3 className="font-heading text-base font-bold text-foreground mb-4">Ingresos por mes</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.ingresos_mensuales}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mes" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }} formatter={(v) => [fmtMoney(v), "Ingresos"]} />
              <Area type="monotone" dataKey="ingresos" stroke="hsl(var(--chart-4))" fill="hsl(var(--chart-4))" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="border border-border bg-card rounded-sm p-5" data-testid="sa-recent-activity">
          <h3 className="font-heading text-base font-bold text-foreground mb-3">Actividad reciente</h3>
          {data.actividad.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Sin actividad registrada aún.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.actividad.map((a) => (
                <li key={a.id} className="py-2.5">
                  <p className="text-sm text-foreground">{a.detail}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.actor} · {fmtDateTime(a.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-border bg-card rounded-sm p-5" data-testid="sa-inactive">
          <h3 className="font-heading text-base font-bold text-foreground mb-3">Empresas sin actividad reciente</h3>
          {data.sin_actividad.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Todas las empresas han ingresado recientemente.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.sin_actividad.map((c) => (
                <li key={c.tenant_id} className="py-2.5 flex items-center justify-between">
                  <span className="text-sm text-foreground">{c.name || c.tenant_id.slice(0, 8)}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(c.last_seen)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
